import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'flowbite-react';
import { toast } from 'react-toastify';
import SpinnerComponent from 'components/spinner';
import { orgSelector } from 'redux/selectors';
import {
  assignIntegration as assignIntegrationApi,
  getOrganizationByFilter,
  getAvailableIntegrations,
  getOrganizationById,
  unassignIntegration as unassignIntegrationApi,
} from 'api/organizationApi';
import { integrationVendorLabels, integrationVendors } from 'utils/constants';
import { messageErrors } from 'utils/utils';
import Select from 'components/select';

const AdminPosIntegrationsPage = () => {
  const [t] = useTranslation();
  const queryClient = useQueryClient();
  const { organizationId } = useSelector(orgSelector);
  const [selectedProviderToAssign, setSelectedProviderToAssign] = useState('');
  const [orgSelect, setOrgSelect] = useState<any>(organizationId ? { value: organizationId, label: '' } : null);
  const selectedOrganizationId = orgSelect?.value || '';

  const handleonError = (error: any, fallback: string) => {
    const message: string = messageErrors(error?.message || String(error) || fallback, t);
    toast.error(message);
  };

  const { data: organizationsData } = useQuery(
    ['getOrganizationsForPosAssign'],
    () => getOrganizationByFilter({ page: 0, limit: 1000, searchQuery: '' }),
    { staleTime: Infinity, retry: false, refetchOnWindowFocus: false },
  );

  const { data: organizationData, isLoading } = useQuery(
    ['getOrganizationDetailForAdminPosIntegrations', selectedOrganizationId],
    () => getOrganizationById(selectedOrganizationId),
    { enabled: !!selectedOrganizationId, staleTime: 0 },
  );

  const { data: availableIntegrationsData } = useQuery(
    ['getAvailableIntegrationsForAdmin'],
    () => getAvailableIntegrations(),
    { enabled: !!selectedOrganizationId },
  );

  const assignIntegrationMutation = useMutation({
    mutationFn: (vendor: string) => assignIntegrationApi(selectedOrganizationId, vendor),
    onSuccess: () => {
      queryClient.invalidateQueries(['getOrganizationDetailForAdminPosIntegrations', selectedOrganizationId]);
      toast.success('Integration assigned');
      setSelectedProviderToAssign('');
    },
    onError: (error: any) => {
      handleonError(error, 'Failed to assign');
    },
  });

  const unassignIntegrationMutation = useMutation({
    mutationFn: (vendor: string) => unassignIntegrationApi(selectedOrganizationId, vendor),
    onSuccess: () => {
      queryClient.invalidateQueries(['getOrganizationDetailForAdminPosIntegrations', selectedOrganizationId]);
      toast.success('Integration unassigned');
    },
    onError: (error: any) => {
      handleonError(error, 'Failed to unassign');
    },
  });

  const organizationOptions = (organizationsData?.data?.entities || []).map((org: any) => ({
    label: org.name,
    value: org.id,
  }));

  useEffect(() => {
    if (orgSelect?.value && !orgSelect?.label && organizationOptions.length > 0) {
      const selected = organizationOptions.find((org: any) => org.value === orgSelect.value);
      if (selected) {
        setOrgSelect(selected);
      }
    }
  }, [orgSelect, organizationOptions]);

  if (isLoading && selectedOrganizationId) return <SpinnerComponent />;

  const assignedVendors = (organizationData?.data?.assignedIntegrationVendors || []).filter(
    (vendor: string) => vendor === integrationVendors.ABC_IGNITE,
  );

  return (
    <div className="main-sub">
      <div className="bg-white w-full p-8 border border-gray-200 rounded-lg">
        <div className="text-xl font-semibold text-gray-900 pb-4">Assigned POS Integrations</div>
        <p className="text-sm text-gray-600 mb-4">
          Assign or remove POS integrations for this organization. Org admins can then configure credentials for assigned integrations.
        </p>
        <div className="mb-4 max-w-md">
          <Select
            placeholder="Select organization"
            options={organizationOptions}
            value={orgSelect}
            onChange={(option: any) => {
              setOrgSelect(option);
              setSelectedProviderToAssign('');
            }}
            isHolderLabel={true}
          />
        </div>
        {!selectedOrganizationId ? (
          <p className="text-gray-600">Select an organization to view and assign POS providers.</p>
        ) : (
          <>
        <div className="text-sm text-gray-700 mb-3">
          Organization: <span className="font-semibold">{organizationData?.data?.name || selectedOrganizationId}</span>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {assignedVendors.map((vendor: string) => (
            <span key={vendor} className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg">
              {integrationVendorLabels[vendor] || vendor}
              <button
                type="button"
                className="text-red-600 hover:text-red-800 text-sm font-medium"
                onClick={() => unassignIntegrationMutation.mutate(vendor)}
                disabled={unassignIntegrationMutation.isLoading}
              >
                Remove
              </button>
            </span>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="rounded-lg border border-gray-300 text-sm"
            value={selectedProviderToAssign}
            onChange={(e) => setSelectedProviderToAssign(e.target.value)}
          >
            <option value="">Select integration to assign</option>
            {(availableIntegrationsData?.data?.vendors || [])
              .filter((v: string) => v === integrationVendors.ABC_IGNITE)
              .filter((v: string) => !assignedVendors.includes(v))
              .map((v: string) => (
                <option key={v} value={v}>
                  {integrationVendorLabels[v] || v}
                </option>
              ))}
          </select>
          <Button
            type="button"
            size="sm"
            className="bg-blue-600 text-white"
            onClick={() => selectedProviderToAssign && assignIntegrationMutation.mutate(selectedProviderToAssign)}
            disabled={!selectedProviderToAssign || assignIntegrationMutation.isLoading}
          >
            Assign
          </Button>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPosIntegrationsPage;
